import { useEffect, useMemo, useState } from "react";
import { FileAudio2, FileImage, FileVideo } from "lucide-react";
import {
  fetchMaterialContentObjectUrl,
  peekMaterialContentObjectUrl,
  type MaterialsContentPurpose,
} from "@/lib/materials-content-cache";
import type { HermesMediaCandidate } from "@/lib/hermes-media-candidates";
import { cn } from "@/lib/utils";

type HermesMediaCandidateCardProps = {
  accountId: string;
  candidate: HermesMediaCandidate;
  compact?: boolean;
  previewMode?: "card" | "dialog";
  onClick?: () => void;
};

export function audioPreviewUsesControls(previewMode: "card" | "dialog"): boolean {
  return previewMode === "dialog";
}

export function videoPreviewUsesControls(previewMode: "card" | "dialog"): boolean {
  return previewMode === "dialog";
}

export function videoPreviewUsesMuted(previewMode: "card" | "dialog"): boolean {
  return previewMode !== "dialog";
}

export function materialPurposeForCandidate(kind: HermesMediaCandidate["kind"]): MaterialsContentPurpose {
  // 图片走缩略图，避免聊天区下整图（可达十余 MB）导致逐张淡入
  return kind === "image" ? "preview" : "content";
}

function useMaterialObjectUrl(
  accountId: string,
  itemId: number,
  purpose: MaterialsContentPurpose,
) {
  const peeked = peekMaterialContentObjectUrl(accountId, itemId, purpose);
  const [objectUrl, setObjectUrl] = useState<string | null>(peeked);
  const [loading, setLoading] = useState(!peeked);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const warm = peekMaterialContentObjectUrl(accountId, itemId, purpose);
    if (warm) {
      setObjectUrl(warm);
      setLoading(false);
      setLoadError(null);
    } else {
      setLoading(true);
      setLoadError(null);
      setObjectUrl(null);
    }

    async function load() {
      try {
        const url = await fetchMaterialContentObjectUrl(accountId, itemId, purpose);
        if (cancelled) return;
        setObjectUrl(url);
      } catch {
        if (!cancelled) {
          setLoadError("预览加载失败");
          setObjectUrl(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [accountId, itemId, purpose]);

  return { objectUrl, loading, loadError };
}

function kindLabel(kind: HermesMediaCandidate["kind"]): string {
  switch (kind) {
    case "image":
      return "图片";
    case "video":
      return "视频";
    case "audio":
      return "音频";
  }
}

export function HermesMediaCandidateCard({
  accountId,
  candidate,
  compact = false,
  previewMode = "card",
  onClick,
}: HermesMediaCandidateCardProps) {
  const purpose = materialPurposeForCandidate(candidate.kind);
  const { objectUrl, loading, loadError } = useMaterialObjectUrl(accountId, candidate.id, purpose);
  const title = candidate.name?.trim() || candidate.slug || "候选媒体";
  const notes = candidate.notes?.trim() || "";
  const label = kindLabel(candidate.kind);
  const interactive = Boolean(onClick);

  const preview = useMemo(() => {
    if (loading) {
      return (
        <div className="flex size-full items-center justify-center text-xs text-muted-foreground">
          加载中
        </div>
      );
    }
    if (loadError || !objectUrl) {
      return (
        <div className="flex size-full flex-col items-center justify-center gap-2 px-3 text-center text-xs text-muted-foreground">
          {candidate.kind === "image" ? <FileImage className="size-5" /> : null}
          {candidate.kind === "video" ? <FileVideo className="size-5" /> : null}
          {candidate.kind === "audio" ? <FileAudio2 className="size-5" /> : null}
          <span>{loadError || "无预览"}</span>
        </div>
      );
    }
    if (candidate.kind === "image") {
      return <img src={objectUrl} alt={title} className="size-full object-cover" />;
    }
    if (candidate.kind === "video") {
      return (
        <video
          src={objectUrl}
          className="size-full object-cover"
          controls={videoPreviewUsesControls(previewMode)}
          muted={videoPreviewUsesMuted(previewMode)}
          playsInline
          preload="metadata"
        />
      );
    }
    if (audioPreviewUsesControls(previewMode)) {
      return <audio src={objectUrl} className="w-full min-w-0" controls preload="metadata" />;
    }
    return (
      <div className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 bg-background/70 px-3 py-2 text-xs text-muted-foreground">
        <FileAudio2 className="size-4 shrink-0" />
        <span>音频预览请在详情中播放</span>
      </div>
    );
  }, [candidate.kind, loadError, loading, objectUrl, previewMode, title]);

  const content = (
    <>
      <div className={cn("overflow-hidden bg-muted", compact ? "aspect-square" : "aspect-video")}>
        <div
          className={cn(
            "flex size-full items-center justify-center",
            candidate.kind === "audio" && "px-3",
          )}
        >
          {preview}
        </div>
      </div>
      <div className="space-y-1 px-3 py-2.5">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
            {label}
          </span>
          <p className="min-w-0 flex-1 truncate text-sm font-medium">{title}</p>
        </div>
        <p className="truncate font-mono text-[11px] text-muted-foreground">{candidate.slug}</p>
        {notes ? (
          <p className={cn("text-xs text-muted-foreground", compact ? "line-clamp-2" : "line-clamp-3")}>
            {notes}
          </p>
        ) : null}
      </div>
    </>
  );

  if (interactive) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          "w-full overflow-hidden rounded-xl border border-border bg-card text-left text-card-foreground shadow-sm transition-colors hover:border-primary/40 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm">
      {content}
    </div>
  );
}
